import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash-es';
import {
  buildMapAssignmentGroups,
  filterMapAssignmentGroups,
  getGroupCheckedState,
  getMapsCheckedState,
} from '../utils/mapAssignment';
import { MAP_GROUP_NAME_OTHERS } from '../utils/map';
import type { RobotScheduleMapGroup } from '../types/RobotScheduleMap.types';
import {
  selectAssociateDevicesToMapData,
  selectAssociateDevicesToMapIsLoading,
  selectAvailableMapGroups,
  selectAvailableMapGroupsIsLoading,
  selectAvailableMaps,
  selectAvailableMapsIsLoading,
} from '../../../../state/machineDetailsPanelRobot/robotSchedule/robotScheduleSelectors';
import { RobotScheduleActions } from '../../../../state/machineDetailsPanelRobot/robotSchedule/robotScheduleSlice';
import type { AvailableMap } from 'app/modules/machine-inventory/ScheduleClient';
import type { Machine } from 'app/modules/machine-inventory/interfaces/Machine.types';

const SEARCH_DEBOUNCE_MS = 500;
const MAPS_FETCH_LIMIT = 10000;

export interface UseMapAssignmentModalParams {
  robot: Machine;
  isOpen: boolean;
  onAssigned: () => void;
  onCancel: () => void;
}

export interface UseMapAssignmentModalResult {
  searchTerm: string;
  selectedMapIds: Set<string>;
  selectedCount: number;
  mapGroups: RobotScheduleMapGroup[];
  isMapsLoading: boolean;
  isMapGroupsLoading: boolean;
  isAssigning: boolean;
  mapGroupsHeaderState: { checked: boolean; indeterminate: boolean };
  mapsHeaderState: { checked: boolean; indeterminate: boolean };
  handleSearchChange: (value: string) => void;
  handleToggleMap: (mapId: string) => void;
  handleToggleGroup: (group: RobotScheduleMapGroup) => void;
  handleToggleAllMapGroups: () => void;
  handleToggleAllMaps: () => void;
  handleClearSelection: () => void;
  handleCancel: () => void;
  handleAssignAndContinue: () => void;
  getGroupCheckedState: (group: RobotScheduleMapGroup) => { checked: boolean; indeterminate: boolean };
}

export const useMapAssignmentModalState = ({
  robot,
  isOpen,
  onAssigned,
  onCancel,
}: UseMapAssignmentModalParams): UseMapAssignmentModalResult => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [selectedMapIds, setSelectedMapIds] = useState<Set<string>>(new Set());

  const availableMapsData = useSelector(selectAvailableMaps);
  const availableMaps = useMemo(() => availableMapsData ?? [], [availableMapsData]);
  const isMapsLoading = useSelector(selectAvailableMapsIsLoading);

  const availableMapGroupsData = useSelector(selectAvailableMapGroups);
  const availableMapGroups = useMemo(() => availableMapGroupsData ?? [], [availableMapGroupsData]);
  const isMapGroupsLoading = useSelector(selectAvailableMapGroupsIsLoading);

  const associateDevicesToMapData = useSelector(selectAssociateDevicesToMapData);
  const isAssigning = useSelector(selectAssociateDevicesToMapIsLoading);

  const unfilteredMapGroups = useMemo(
    () => buildMapAssignmentGroups(availableMapGroups, availableMaps, i18n.language),
    [availableMapGroups, availableMaps, i18n.language]
  );

  const mapGroups = useMemo(
    () => filterMapAssignmentGroups(unfilteredMapGroups, debouncedSearchTerm),
    [unfilteredMapGroups, debouncedSearchTerm]
  );

  const mapGroupsHeaderState = useMemo(() => {
    const groupedMaps = unfilteredMapGroups
      .filter(group => group.groupId !== MAP_GROUP_NAME_OTHERS)
      .flatMap(group => group.maps);

    return getMapsCheckedState(groupedMaps, selectedMapIds);
  }, [unfilteredMapGroups, selectedMapIds]);

  const mapsHeaderState = useMemo(
    () => getMapsCheckedState(availableMaps, selectedMapIds),
    [availableMaps, selectedMapIds]
  );

  const resetSelection = useCallback((): void => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setSelectedMapIds(new Set());
  }, []);

  const debouncedSetSearchTerm = useMemo(
    () => debounce((value: string) => setDebouncedSearchTerm(value), SEARCH_DEBOUNCE_MS),
    []
  );

  useEffect(() => (): void => debouncedSetSearchTerm.cancel(), [debouncedSetSearchTerm]);

  useEffect(() => {
    if (isOpen) {
      dispatch(
        RobotScheduleActions.listAvailableMapsRequest({
          filter: { limit: MAPS_FETCH_LIMIT },
        })
      );
      dispatch(
        RobotScheduleActions.listAvailableMapGroupsRequest({
          filter: { limit: MAPS_FETCH_LIMIT },
        })
      );
    }
  }, [dispatch, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      debouncedSetSearchTerm.cancel();
      resetSelection();
      dispatch(RobotScheduleActions.associateDevicesToMapReset());
    }
  }, [isOpen, dispatch, resetSelection, debouncedSetSearchTerm]);

  useEffect(() => {
    if (associateDevicesToMapData?.success) {
      dispatch(RobotScheduleActions.associateDevicesToMapReset());
      resetSelection();
      onAssigned();
    }
  }, [associateDevicesToMapData, dispatch, resetSelection, onAssigned]);

  const handleSearchChange = useCallback(
    (value: string): void => {
      setSearchTerm(value);
      debouncedSetSearchTerm(value);
    },
    [debouncedSetSearchTerm]
  );

  const handleToggleMap = useCallback((mapId: string): void => {
    setSelectedMapIds(current => {
      const next = new Set(current);

      if (next.has(mapId)) {
        next.delete(mapId);
      } else {
        next.add(mapId);
      }

      return next;
    });
  }, []);

  const toggleMapsSelection = useCallback((maps: AvailableMap[]): void => {
    setSelectedMapIds(current => {
      const { checked } = getMapsCheckedState(maps, current);
      const next = new Set(current);

      maps.forEach(map => {
        if (checked) {
          next.delete(map.id);
        } else {
          next.add(map.id);
        }
      });

      return next;
    });
  }, []);

  const handleToggleGroup = useCallback(
    (group: RobotScheduleMapGroup): void => toggleMapsSelection(group.maps),
    [toggleMapsSelection]
  );

  const handleToggleAllMapGroups = useCallback((): void => {
    const groupedMaps = unfilteredMapGroups
      .filter(group => group.groupId !== MAP_GROUP_NAME_OTHERS)
      .flatMap(group => group.maps);

    toggleMapsSelection(groupedMaps);
  }, [toggleMapsSelection, unfilteredMapGroups]);

  const handleToggleAllMaps = useCallback((): void => {
    toggleMapsSelection(availableMaps);
  }, [toggleMapsSelection, availableMaps]);

  const handleClearSelection = useCallback((): void => {
    setSelectedMapIds(new Set());
  }, []);

  const handleCancel = useCallback((): void => {
    resetSelection();
    dispatch(RobotScheduleActions.associateDevicesToMapReset());
    onCancel();
  }, [resetSelection, dispatch, onCancel]);

  const handleAssignAndContinue = useCallback((): void => {
    if (selectedMapIds.size === 0) {
      return;
    }

    dispatch(
      RobotScheduleActions.associateDevicesToMapRequest({
        mapIds: Array.from(selectedMapIds),
        materialNumber: robot.materialNumber,
        serialNumber: robot.serialNumber,
      })
    );
  }, [dispatch, selectedMapIds, robot.materialNumber, robot.serialNumber]);

  const getGroupCheckedStateForGroup = useCallback(
    (group: RobotScheduleMapGroup) => getGroupCheckedState(group, selectedMapIds),
    [selectedMapIds]
  );

  return {
    searchTerm,
    selectedMapIds,
    selectedCount: selectedMapIds.size,
    mapGroups,
    isMapsLoading,
    isMapGroupsLoading,
    isAssigning,
    mapGroupsHeaderState,
    mapsHeaderState,
    handleSearchChange,
    handleToggleMap,
    handleToggleGroup,
    handleToggleAllMapGroups,
    handleToggleAllMaps,
    handleClearSelection,
    handleCancel,
    handleAssignAndContinue,
    getGroupCheckedState: getGroupCheckedStateForGroup,
  };
};
